import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ShoppingBag, Settings, Package, FileText, Inbox, Truck, CreditCard, Eye, EyeOff, Plus, ChevronRight, Check, X, Clock, Send, CheckCheck, AlertCircle, ExternalLink } from 'lucide-react';
import { usePermissions } from '../../../contexts/PermissionsContext';
import { useAuth } from '../../../contexts/AuthContext';
import { useToast } from '../../../components/Toast';
import Modal from '../../../components/Modal';
import api from '../../../services/api';

const ORDER_STATUS = {
  new: { label: 'Nowe', color: 'bg-blue-100 text-blue-700' },
  paid: { label: 'Oplacone', color: 'bg-green-100 text-green-700' },
  processing: { label: 'Realizacja', color: 'bg-amber-100 text-amber-700' },
  shipped: { label: 'Wyslane', color: 'bg-indigo-100 text-indigo-700' },
  delivered: { label: 'Dostarczone', color: 'bg-emerald-100 text-emerald-700' },
  cancelled: { label: 'Anulowane', color: 'bg-red-100 text-red-700' },
};

const INQUIRY_STATUS = {
  new: { label: 'Nowe', color: 'bg-blue-100 text-blue-700' },
  read: { label: 'Przeczytane', color: 'bg-gray-100 text-gray-600' },
  replied: { label: 'Odpowiedziane', color: 'bg-green-100 text-green-700' },
  quoted: { label: 'Wycenione', color: 'bg-purple-100 text-purple-700' },
  closed: { label: 'Zamkniete', color: 'bg-gray-100 text-gray-500' },
};

// ==================== SETTINGS TAB ====================

function SettingsTab({ settings, pricelists, onSave, toast, canEdit }) {
  const { t } = useTranslation();
  const [form, setForm] = useState(settings || {});
  const ic = "w-full px-3 py-2 border border-gray-300 rounded-lg text-sm";
  const lc = "block text-xs font-medium text-gray-600 mb-1";

  const handleTogglePricelist = async (plId, published) => {
    try {
      await api.put(`/shop/pricelists/${plId}/shop`, { is_shop_published: published });
      toast.success(published ? t('shop.pricelistPublished') : t('shop.pricelistHidden'));
    } catch { toast.error(t('common.error')); }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white border rounded-lg p-4 space-y-4">
        <h3 className="font-medium text-gray-800">Ogolne</h3>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.is_enabled} onChange={e => setForm({...form, is_enabled: e.target.checked})} />
          Sklep wlaczony
        </label>
        <div className="grid grid-cols-3 gap-3">
          <div><label className={lc}>Tryb sklepu</label>
            <select value={form.shop_mode||'mixed'} onChange={e => setForm({...form, shop_mode: e.target.value})} className={ic}>
              <option value="ecommerce">E-commerce (koszyk + platnosci)</option>
              <option value="inquiry">Katalog z zapytaniami (B2B)</option>
              <option value="mixed">Mieszany (per produkt)</option>
            </select></div>
          <div><label className={lc}>Tytul sklepu</label>
            <input type="text" value={form.shop_title||''} onChange={e => setForm({...form, shop_title: e.target.value})} className={ic} /></div>
          <div><label className={lc}>Produkty/strona</label>
            <select value={form.products_per_page||12} onChange={e => setForm({...form, products_per_page: parseInt(e.target.value)})} className={ic}>
              <option value={12}>12</option><option value={24}>24</option><option value={48}>48</option>
            </select></div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div><label className={lc}>Waluta</label>
            <select value={form.currency_display||'PLN'} onChange={e => setForm({...form, currency_display: e.target.value})} className={ic}>
              <option value="PLN">PLN</option><option value="EUR">EUR</option><option value="USD">USD</option>
            </select></div>
          <div><label className={lc}>Domyslny VAT</label>
            <select value={form.vat_rate||23} onChange={e => setForm({...form, vat_rate: parseFloat(e.target.value)})} className={ic}>
              <option value={23}>23%</option><option value={8}>8%</option><option value={5}>5%</option><option value={0}>0%</option>
            </select></div>
          <div><label className={lc}>Email zapytan</label>
            <input type="email" value={form.inquiry_email||''} onChange={e => setForm({...form, inquiry_email: e.target.value})} className={ic} /></div>
        </div>
        <div className="flex gap-4">
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.show_prices!==false} onChange={e => setForm({...form, show_prices: e.target.checked})} /> Pokazuj ceny</label>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.hide_prices_for_guests||false} onChange={e => setForm({...form, hide_prices_for_guests: e.target.checked})} /> Ukryj ceny dla gosci</label>
        </div>
        {canEdit && <button onClick={() => onSave(form)} className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm">{t('shop.saveSettings')}</button>}
      </div>

      {/* Pricelists in shop */}
      <div className="bg-white border rounded-lg p-4">
        <h3 className="font-medium text-gray-800 mb-3">Cenniki w sklepie</h3>
        <div className="space-y-2">
          {pricelists.map(pl => (
            <div key={pl.id} className="flex items-center justify-between py-2 border-b last:border-0">
              <div className="flex items-center gap-3">
                <button onClick={() => handleTogglePricelist(pl.id, !pl.is_shop_published)} className="flex-shrink-0">
                  {pl.is_shop_published ? <Eye size={16} className="text-green-600" /> : <EyeOff size={16} className="text-gray-300" />}
                </button>
                <div>
                  <span className={`text-sm font-medium ${pl.is_shop_published ? 'text-gray-800' : 'text-gray-400'}`}>{pl.name}</span>
                  <span className="text-xs text-gray-400 ml-2">{pl.visible_items} produktow</span>
                </div>
              </div>
              {pl.is_shop_published && <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">{pl.shop_category || 'Bez kategorii'}</span>}
            </div>
          ))}
        </div>
      </div>

      {/* Shipping & payments (e-commerce) */}
      {(form.shop_mode === 'ecommerce' || form.shop_mode === 'mixed') && (
        <div className="bg-white border rounded-lg p-4 space-y-4">
          <h3 className="font-medium text-gray-800">Dostawa i platnosci</h3>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={lc}>Min. wartosc zamowienia (netto)</label>
              <input type="number" step="0.01" value={form.min_order_value||0} onChange={e => setForm({...form, min_order_value: parseFloat(e.target.value)})} className={ic} /></div>
            <div><label className={lc}>Darmowa wysylka od (netto)</label>
              <input type="number" step="0.01" value={form.free_shipping_above||''} onChange={e => setForm({...form, free_shipping_above: e.target.value ? parseFloat(e.target.value) : null})} className={ic} placeholder="Brak" /></div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><label className={lc}>Dane sprzedawcy</label><input type="text" value={form.seller_name||''} onChange={e => setForm({...form, seller_name: e.target.value})} className={ic} placeholder="Nazwa firmy" /></div>
            <div><label className={lc}>NIP sprzedawcy</label><input type="text" value={form.seller_nip||''} onChange={e => setForm({...form, seller_nip: e.target.value})} className={ic} /></div>
            <div><label className={lc}>Prefix faktur</label><input type="text" value={form.invoice_prefix||'FV/'} onChange={e => setForm({...form, invoice_prefix: e.target.value})} className={ic} /></div>
          </div>
        </div>
      )}
    </div>
  );
}

// ==================== ORDERS TAB ====================

function OrdersTab({ toast }) {
  const { t } = useTranslation();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);

  useEffect(() => {
    api.get('/shop/orders').then(r => { setOrders(r.data.orders); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const updateStatus = async (id, status) => {
    try {
      await api.put(`/shop/orders/${id}`, { status });
      setOrders(prev => prev.map(o => o.id === id ? { ...o, status } : o));
      toast.success(t('shop.statusChanged'));
    } catch { toast.error(t('common.error')); }
  };

  if (loading) return <div className="p-8 text-gray-400">{t('common.loading')}</div>;

  return (
    <div>
      {orders.length === 0 ? (
        <div className="text-center py-16 text-gray-400"><Package size={48} className="mx-auto mb-4 opacity-50" /><p>{t('shop.noOrders')}</p></div>
      ) : (
        <div className="bg-white border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead><tr className="bg-gray-50 border-b text-xs text-gray-500 uppercase">
              <th className="text-left px-4 py-3">Nr</th><th className="text-left px-4 py-3">Data</th>
              <th className="text-left px-4 py-3">Klient</th><th className="text-right px-4 py-3">Kwota</th>
              <th className="text-left px-4 py-3">Status</th><th className="w-20"></th>
            </tr></thead>
            <tbody>
              {orders.map(o => {
                const s = ORDER_STATUS[o.status] || ORDER_STATUS.new;
                return (
                  <tr key={o.id} className="border-b hover:bg-gray-50">
                    <td className="px-4 py-3 font-mono text-xs text-gray-600">{o.order_number}</td>
                    <td className="px-4 py-3 text-xs text-gray-500">{new Date(o.created_at).toLocaleDateString('pl-PL')}</td>
                    <td className="px-4 py-3"><div className="text-gray-800">{o.customer_name||o.customer_email}</div>
                      {o.customer_company && <div className="text-xs text-gray-400">{o.customer_company}</div>}</td>
                    <td className="px-4 py-3 text-right font-medium">{Number(o.total).toLocaleString('pl-PL', {minimumFractionDigits:2})} zl</td>
                    <td className="px-4 py-3">
                      <select value={o.status} onChange={e => updateStatus(o.id, e.target.value)}
                        className={`px-2 py-0.5 rounded text-xs font-medium border-0 ${s.color}`}>
                        {Object.entries(ORDER_STATUS).map(([k,v]) => <option key={k} value={k}>{v.label}</option>)}
                      </select>
                    </td>
                    <td className="px-4 py-3"><button onClick={() => setSelectedOrder(o)} className="text-xs text-blue-600 hover:underline">Szczegoly</button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {selectedOrder && (
        <Modal isOpen={!!selectedOrder} onClose={() => setSelectedOrder(null)} title={`Zamowienie ${selectedOrder.order_number}`} maxWidth="max-w-2xl">
          <div className="space-y-3 text-sm">
            <div className="grid grid-cols-2 gap-4">
              <div><strong>Klient:</strong> {selectedOrder.customer_name}<br/>{selectedOrder.customer_email}<br/>{selectedOrder.customer_phone}</div>
              <div><strong>Dostawa:</strong> {selectedOrder.shipping_method}<br/>{selectedOrder.shipping_street}, {selectedOrder.shipping_zip} {selectedOrder.shipping_city}</div>
            </div>
            <div><strong>Produkty:</strong></div>
            <table className="w-full text-xs border">
              <thead><tr className="bg-gray-50"><th className="p-2 text-left">Produkt</th><th className="p-2">Ilosc</th><th className="p-2 text-right">Cena</th><th className="p-2 text-right">Wartosc</th></tr></thead>
              <tbody>{(selectedOrder.items||[]).map((item, i) => (
                <tr key={i} className="border-t"><td className="p-2">{item.name}</td><td className="p-2 text-center">{item.quantity} {item.unit}</td>
                  <td className="p-2 text-right">{item.unit_price}</td><td className="p-2 text-right">{item.total}</td></tr>
              ))}</tbody>
            </table>
            <div className="text-right space-y-1">
              <div>Netto: {Number(selectedOrder.subtotal).toLocaleString('pl-PL', {minimumFractionDigits:2})} zl</div>
              <div>VAT: {Number(selectedOrder.vat_amount).toLocaleString('pl-PL', {minimumFractionDigits:2})} zl</div>
              <div>Wysylka: {Number(selectedOrder.shipping_cost).toLocaleString('pl-PL', {minimumFractionDigits:2})} zl</div>
              <div className="font-bold text-lg">Razem: {Number(selectedOrder.total).toLocaleString('pl-PL', {minimumFractionDigits:2})} zl</div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ==================== INQUIRIES TAB ====================

function InquiriesTab({ toast }) {
  const { t } = useTranslation();
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/shop/inquiries').then(r => { setInquiries(r.data.inquiries); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const updateStatus = async (id, status) => {
    try { await api.put(`/shop/inquiries/${id}`, { status }); setInquiries(prev => prev.map(q => q.id === id ? {...q, status} : q)); }
    catch { toast.error(t('common.error')); }
  };

  if (loading) return <div className="p-8 text-gray-400">{t('common.loading')}</div>;

  return (
    <div>
      {inquiries.length === 0 ? (
        <div className="text-center py-16 text-gray-400"><Inbox size={48} className="mx-auto mb-4 opacity-50" /><p>{t('shop.noInquiries')}</p></div>
      ) : (
        <div className="space-y-2">
          {inquiries.map(q => {
            const s = INQUIRY_STATUS[q.status] || INQUIRY_STATUS.new;
            return (
              <div key={q.id} className="bg-white border rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <span className="font-medium text-gray-800">{q.customer_name || q.customer_email}</span>
                    {q.customer_company && <span className="text-xs text-gray-400 ml-2">{q.customer_company}</span>}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-400">{new Date(q.created_at).toLocaleDateString('pl-PL')}</span>
                    <select value={q.status} onChange={e => updateStatus(q.id, e.target.value)}
                      className={`px-2 py-0.5 rounded text-xs font-medium border-0 ${s.color}`}>
                      {Object.entries(INQUIRY_STATUS).map(([k,v]) => <option key={k} value={k}>{v.label}</option>)}
                    </select>
                  </div>
                </div>
                <div className="text-xs text-gray-600 space-y-1">
                  {(q.items||[]).map((item, i) => (
                    <span key={i} className="inline-block mr-2 px-2 py-0.5 bg-gray-100 rounded">{item.name} x{item.quantity} {item.unit}</span>
                  ))}
                </div>
                {q.message && <div className="mt-2 text-sm text-gray-500 italic">"{q.message}"</div>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ==================== MAIN ====================

export default function ShopPage() {
  const { t } = useTranslation();
  const { canView, canCreate, canEdit, canDelete } = usePermissions();
  const { user } = useAuth();
  const toast = useToast();
  const [tab, setTab] = useState('settings');
  const [settings, setSettings] = useState(null);
  const [pricelists, setPricelists] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const r = await api.get('/shop/settings');
      setSettings(r.data.settings);
      setPricelists(r.data.pricelists);
    } catch { toast.error(t('common.error')); }
    finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const handleSaveSettings = async (data) => {
    try { await api.put('/shop/settings', data); toast.success(t('common.saved')); load(); }
    catch { toast.error(t('common.error')); }
  };

  if (!canView('shop')) return <div className="p-8 text-center text-gray-400"><ShoppingBag size={48} className="mx-auto mb-4 opacity-50" /><p>{t('common.noAccess')}</p></div>;
  if (loading) return <div className="p-8 text-gray-400">{t('common.loading')}</div>;

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-3">
            <ShoppingBag size={28} className="text-red-600" /> {t('shop.title')}
          </h1>
          <p className="text-gray-500 mt-1">
            {settings?.is_enabled ? <span className="text-green-600">Wlaczony</span> : <span className="text-gray-400">Wylaczony</span>}
            {settings?.shop_mode && <span className="ml-2 text-xs text-gray-400">({settings.shop_mode})</span>}
          </p>
        </div>
        <a href={`/site/${user?.tenant_slug || 'default'}/sklep`} target="_blank" rel="noopener noreferrer"
          className="flex items-center gap-1 px-3 py-1.5 border border-blue-300 text-blue-600 hover:bg-blue-50 rounded-lg text-sm">
          <ExternalLink size={14} /> Podgląd sklepu
        </a>
      </div>

      <div className="flex gap-1 border-b mb-4">
        {[
          { k: 'settings', l: t('shop.settingsTab'), icon: Settings },
          { k: 'orders', l: t('shop.ordersTab'), icon: Package },
          { k: 'inquiries', l: t('shop.inquiriesTab'), icon: Inbox },
        ].map(t => (
          <button key={t.k} onClick={() => setTab(t.k)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition ${tab === t.k ? 'border-red-600 text-red-600' : 'border-transparent text-gray-500'}`}>
            <t.icon size={16} /> {t.l}
          </button>
        ))}
      </div>

      {tab === 'settings' && <SettingsTab settings={settings} pricelists={pricelists} onSave={handleSaveSettings} toast={toast} canEdit={canEdit('shop')} />}
      {tab === 'orders' && <OrdersTab toast={toast} />}
      {tab === 'inquiries' && <InquiriesTab toast={toast} />}
    </div>
  );
}
